<script setup>
import { useAxiosClient } from '@/composable/useFetch'
import Doughnut from '../Chart/doughnut.vue'
import { computed, ref, watch } from 'vue'
import { i18n } from '@/i18n'
import ReportChartTypeSelector from './ReportChartTypeSelector.vue'

const LABEL_KEY = 'warehouseName'
const ENDPOINT = '/bi/dashboard2/dash_ord_date/chart/order_by_warehouse';

const props = defineProps({
  colorPalette: {
    type: Array,
    default: () => [
      '#FF0A5495',
      '#007BFF95',
      '#31773F95',
      '#FFC10795',
      '#503D9A95',
      '#43CBA495',
    ],
  },
  filter: {
    type: Object,
    default: () => ({}),
  },
  valueFormatting: {
    type: Function,
    default: value => value,
  },
})

/** @type {import('chart.js').ChartOptions} */
const charConfig = {
  responsive: true,
  maintainAspectRatio: false,
  legend: {
    display: false,
    position: 'right',
    labels: {
      boxWidth: 12,
      usePointStyle: true,
      padding: 16,
    },
  },
  tooltips: {
    enabled: true,
    displayColors: false,
    titleMarginBottom: 12,
    bodySpacing: 8,
    callbacks: {
      title: (t, d) => `${d.labels[t[0].index]}`,
      label: (t, d) => {
        return d.datasets[t.datasetIndex].tooltip?.[t.index] || t.value
      },
    },
  },
  cutoutPercentage: 80,
}

const DEFAULT_TYPE = 'GMV'
const type = ref(DEFAULT_TYPE)
watch(props.filter, () => (type.value = DEFAULT_TYPE), { deep: true })

const state = useAxiosClient(
  ENDPOINT,
  () => ({
    params: {
      ...props.filter,
      sorting_type: (type.value || 'GMV').toUpperCase(),
    },
  }),
  {
    watch: [props.filter, type],
    immediate: true,
    initialData: [],
    transform(data) {
      const valueMapper = {
        GMV: 'salesAmount',
        NMV: 'salesAmount',
        ORDERS: 'countAggr',
      }

      return data.map((item, index) => {
        const valueType = valueMapper[type.value] ?? 'salesAmount'
        const value = item[valueType] || 0

        return {
          ...item,
          value,
          tooltip: i18n.tc(
            `dashboard.${type.value.toLowerCase()}.tooltip`,
            value,
            { value: props.valueFormatting(value) },
          ),
          color: props.colorPalette[index] ?? '#CCCCCC',
          formattedValue: props.valueFormatting(value),
        }
      })
    },
  },
)

const excludedList = ref([])
const chartData = computed(() => {
  const values = (state.state.value || []).filter(
    (item, index) => !excludedList.value.includes(index),
  )

  return {
    labels: values.map(item => item[LABEL_KEY]),
    datasets: [
      {
        label: 'Order',
        backgroundColor: values.map(item => item.color),
        data: values.map(item => item.value || 0),
        tooltip: values.map(item => item.tooltip),
        barThickness: 3,
      },
    ],
  }
})

function addOrRemoveItem(index) {
  if (excludedList.value.includes(index)) {
    excludedList.value = excludedList.value.filter(item => item !== index)
  } else {
    excludedList.value.push(index)
  }
}
</script>

<template>
  <div>
    <div class="mb-5">
      <h2>
        {{ $t('dashboard.top_warehouse_title') }}

        <ReportChartTypeSelector v-model="type" />
      </h2>
      <div class="subtitle">
        {{ $t('dashboard.top_warehouse_subtitle') }}
      </div>
    </div>

    <div class="chart-container">
      <div
        class="text-center d-flex items-center justify-content-center"
        style="flex-shrink: 0; flex-grow: 0; width: 250px"
      >
        <a-skeleton
          :loading="state.isLoading.value"
          active
          :paragraph="false"
          :title="false"
          avatar
          class="chart-loading"
        >
          <Doughnut
            :chart-data="chartData"
            :options="charConfig"
            style="height: 200px; width: 200px"
          />
        </a-skeleton>
      </div>
      <div class="">
        <a-skeleton
          :loading="state.isLoading.value"
          active
          :paragraph="{ rows: 5 }"
          :title="false"
          class="chart-legend-loading"
        >
          <div class="item-list">
            <label
              v-for="(item, index) in state.state.value"
              :key="item[LABEL_KEY]"
              :style="{ '--color': item.color }"
              :class="{ disabled: excludedList.includes(index) }"
              @click="addOrRemoveItem(index)"
            >
              {{ item[LABEL_KEY] }}
            </label>
          </div>
        </a-skeleton>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.chart-container {
  width: 100%;
  display: flex;
  gap: 1rem;

  > div:nth-child(1) {
    flex-shrink: 0;
    flex-grow: 0;
    width: 250px;
  }

  > div:nth-child(2) {
    flex-grow: 1;
    width: 100%;
  }

  .item-list {
    display: grid;
    grid-template-columns: repeat(1, minmax(0, 1fr));
    // grid-template-columns: repeat(2, minmax(0, 1fr));
    // grid-template-rows: repeat(5, minmax(0, 1fr));
    // grid-auto-flow: column dense;

    // gap: 0.5rem;
  }
}

.chart-loading::v-deep {
  .ant-skeleton-avatar {
    height: 200px !important;
    width: 200px !important;
  }
}

.chart-legend-loading::v-deep {
  .ant-skeleton-paragraph {
    li {
      margin-bottom: .75rem;
      height: 14px;
      width: 100% !important;

      +li {
        margin-top: 0;
      }
    }
  }
}

label {
  cursor: pointer;
  display: block;
  position: relative;
  font-size: 0.775rem;
  color: var(--text-color-secondary);
  display: flex;
  align-items: flex-start;
  margin-bottom: 0.25rem;
  font-weight: 300;

  &.disabled {
    text-decoration: line-through;
  }

  &:hover {
    color: var(--text-color);
  }

  &:before {
    content: '';
    display: inline-block;
    width: 11px;
    height: 11px;
    flex-shrink: 0;
    background-color: var(--color);
    left: 0;
    margin-top: 0.275rem;
    margin-right: 0.5rem;
  }
}
</style>
